import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, AlertIcon, Badge, Button, HStack, Input, Link, SimpleGrid, Spinner, Stack, Text } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import {
  AdminArticleSummary,
  apiAdminListArticles,
  apiAdminPublishArticle,
  apiAdminUnpublishArticle,
  apiAdminDeleteArticle,
  ApiErrorResponse,
} from '../api/client';
import PortalPageShell from './PortalPageShell';

export default function AdminArticles() {
  const [articles, setArticles] = useState<AdminArticleSummary[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'published' | 'unpublished'>('all');

  const loadArticles = useCallback(async (status: 'all' | 'published' | 'unpublished') => {
    setLoading(true);
    const data: AdminArticleSummary[] | ApiErrorResponse = await apiAdminListArticles(status);
    if (Array.isArray(data)) {
      setArticles(data);
      setError('');
    } else {
      setArticles([]);
      setError(data?.message || 'Unable to load articles right now.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadArticles(statusFilter);
  }, [loadArticles, statusFilter]);

  const filteredArticles = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return articles;

    return articles.filter(article => {
      const title = article.title?.toLowerCase() || '';
      const excerpt = article.excerpt?.toLowerCase() || '';
      const filename = article.pdf_filename?.toLowerCase() || '';
      return title.includes(term) || excerpt.includes(term) || filename.includes(term);
    });
  }, [articles, search]);

  const formatDateTime = (value?: string) => {
    if (!value) return '';
    const date = new Date(value.replace(' ', 'T'));
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
  };

  const handleTogglePublish = async (article: AdminArticleSummary) => {
    if (article.published) {
      await apiAdminUnpublishArticle(article.id);
    } else {
      await apiAdminPublishArticle(article.id);
    }

    loadArticles(statusFilter);
  };

  const handleDeleteArticle = async (article: AdminArticleSummary) => {
    if (!window.confirm(`Delete "${article.title}" permanently?`)) {
      return;
    }

    await apiAdminDeleteArticle(article.id);
    loadArticles(statusFilter);
  };

  const handleStatusChange = (next: 'all' | 'published' | 'unpublished') => {
    if (next === statusFilter) return;
    setStatusFilter(next);
    loadArticles(next);
  };

  if (loading) {
    return (
      <PortalPageShell title="Manage Articles" subtitle="View and manage portal articles.">
        <Stack align="center" py={12} spacing={4}>
          <Spinner size="xl" color="blue.500" />
          <Text color="gray.600">Loading articles...</Text>
        </Stack>
      </PortalPageShell>
    );
  }

  return (
    <PortalPageShell title="Manage Articles" subtitle="View and manage portal articles.">
      <Stack spacing={5}>
        {error ? (
          <Alert status="warning" borderRadius="lg">
            <AlertIcon />
            {error}
          </Alert>
        ) : null}

        <HStack spacing={3} flexWrap="wrap">
          <Text fontWeight="600" color="gray.700">Status:</Text>
          <HStack spacing={2}>
            <Button
              size="sm"
              variant={statusFilter === 'all' ? 'solid' : 'outline'}
              colorScheme="blue"
              onClick={() => handleStatusChange('all')}
            >
              All
            </Button>
            <Button
              size="sm"
              variant={statusFilter === 'published' ? 'solid' : 'outline'}
              colorScheme="green"
              onClick={() => handleStatusChange('published')}
            >
              Published
            </Button>
            <Button
              size="sm"
              variant={statusFilter === 'unpublished' ? 'solid' : 'outline'}
              colorScheme="orange"
              onClick={() => handleStatusChange('unpublished')}
            >
              Unpublished
            </Button>
          </HStack>
        </HStack>

        <Input
          placeholder="Search by title, summary, or filename"
          value={search}
          onChange={e => setSearch(e.target.value)}
          maxW="420px"
        />

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
        {filteredArticles.map(article => (
          <Stack key={article.id} p={5} borderWidth="1px" borderColor="gray.200" borderRadius="xl" spacing={3}>
            <Badge width="fit-content" colorScheme={article.content_type === 'pdf' ? 'purple' : 'green'}>
              {article.content_type}
            </Badge>
            <Badge width="fit-content" colorScheme={article.published ? 'green' : 'gray'}>
              {article.published ? 'published' : 'draft'}
            </Badge>
            <Link
              as={RouterLink}
              to={`/admin/articles/${article.id}`}
              target="_blank"
              rel="noopener noreferrer"
              fontWeight="700"
              fontSize="lg"
              color="blue.700"
            >
              {article.title}
            </Link>
            <Text color="gray.600">{article.excerpt || 'No excerpt available.'}</Text>
            {article.pdf_filename ? <Text color="gray.500" fontSize="sm">PDF: {article.pdf_filename}</Text> : null}
            {article.published_at ? (
              <Text color="gray.500" fontSize="sm">
                Published at: {formatDateTime(article.published_at)}
              </Text>
            ) : null}
            <HStack spacing={3} pt={2} flexWrap="wrap">
              <Button as={RouterLink} to={`/admin/articles/${article.id}/edit`} size="sm" colorScheme="blue" variant="outline">
                Edit
              </Button>
              <Button size="sm" colorScheme={article.published ? 'orange' : 'green'} variant="outline" onClick={() => handleTogglePublish(article)}>
                {article.published ? 'Unpublish' : 'Publish'}
              </Button>
              <Button size="sm" colorScheme="red" variant="outline" onClick={() => handleDeleteArticle(article)}>
                Delete
              </Button>
            </HStack>
          </Stack>
        ))}
        </SimpleGrid>
        {!error && filteredArticles.length === 0 ? <Text color="gray.600">No articles match your search.</Text> : null}
      </Stack>
    </PortalPageShell>
  );
}
